import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { AzureStorageManage } from "../../../helper/azureStorageManage";
import ContentWrapper from "../../../components/common/ContentWrapper";
import CardBody from "../../../components/common/CardBody";
import { VideoProgressService } from "../../../service/manage/video_streaming/video_progress_service";
import "./ManageVideoStream.css";

// Constants
const PROGRESS_UPDATE_INTERVAL = 5000;
const PROGRESS_SAVE_THRESHOLD = 5;

// Custom hooks
const useSessionStorage = (key, defaultValue = "") => {
  return useMemo(() => sessionStorage.getItem(key) || defaultValue, [key, defaultValue]);
};

const useProgressTracking = (videoRef, selectedVideo, onProgressUpdate) => {
  const intervalRef = useRef(null);

  const startTracking = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (videoRef.current && selectedVideo) {
        const currentTime = videoRef.current.currentTime;
        onProgressUpdate(selectedVideo.video_id, currentTime);
      }
    }, PROGRESS_UPDATE_INTERVAL);
  }, [videoRef, selectedVideo, onProgressUpdate]);

  const stopTracking = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    // Final update on stop
    if (videoRef.current && selectedVideo) {
      const currentTime = videoRef.current.currentTime;
      onProgressUpdate(selectedVideo.video_id, currentTime);
    }
  }, [videoRef, selectedVideo, onProgressUpdate]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return { startTracking, stopTracking };
};

// Utility functions
const formatFileSize = (bytes) => {
  if (!bytes) return "N/A";
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const formatDate = (dateString) => 
  new Date(dateString).toLocaleDateString("en-US", {
    month: "short", 
    day: "numeric", 
    year: "numeric"
  });

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "0:00";
  const totalSeconds = parseFloat(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${remainingSeconds.toFixed(2).padStart(5, "0")}`;
};

const getVideoProgressPercentage = (watchedDuration, totalDuration) => {
  const duration = parseFloat(totalDuration) || 0;
  return duration > 0 ? Math.min((watchedDuration / duration) * 100, 100) : 0;
};

// Helper function to check if a video is completed
const isVideoCompleted = (watchedDuration, totalDuration) => {
  const progressPercentage = getVideoProgressPercentage(watchedDuration, totalDuration);
  return progressPercentage >= 100;
};

function ManageVideoStream({ topicData, onBack, state }) {
  // State management
  const [videoState, setVideoState] = useState({
    selectedVideo: null,
    videoStreamUrl: null,
    videoMetadata: null,
    availableQualities: {},
    selectedQuality: "auto",
    progress: 0,
    buffered: 0,
    isLoading: false,
    error: null,
  });

  const [progressState, setProgressState] = useState({
    videoProgress: {},
    topicProgress: 0,
    isProgressLoading: false,
  });

  const [permissions, setPermissions] = useState({
    addVisibility: false,
    editVisibility: false,
    viewVisibility: false,
    deleteVisibility: false,
  });

  // Session and user data
  const userId = useSessionStorage("manage_user_id");
  const userTableId = useSessionStorage("table_log_id");
  const [nodeId, setNodeId] = useState(null);

  // Refs for performance and data management
  const videoRef = useRef(null);
  const streamUrlCache = useRef(new Map());
  const lastSavedProgress = useRef({});
  const existingProgressRecords = useRef(new Set());

  // Computed values
  const videoCount = useMemo(() => topicData?.videos?.length || 0, [topicData?.videos]);
  const hasValidUserData = useMemo(() => userId && userTableId, [userId, userTableId]);

  // NEW: Calculate completed videos count
  const completedVideosCount = useMemo(() => {
    if (!topicData?.videos || !progressState.videoProgress) return 0;
    
    return topicData.videos.filter(video => {
      const watchedDuration = progressState.videoProgress[video.video_id] || 0;
      return isVideoCompleted(watchedDuration, video.duration);
    }).length;
  }, [topicData?.videos, progressState.videoProgress]);

  // Progress service wrapper with error handling
  const progressService = useMemo(() => ({
    async getVideoProgress(userId, videoId) {
      try {
        return await VideoProgressService.getVideoProgress(userId, videoId);
      } catch (error) {
        console.warn(`Failed to get video progress for ${videoId}:`, error);
        return null;
      }
    },

    async getTopicProgress(topicId, userId) {
      try {
        return await VideoProgressService.getTopicProgress(topicId, userId);
      } catch (error) {
        console.warn(`Failed to get topic progress for ${topicId}:`, error);
        return null;
      }
    },

    async saveVideoProgress(payload, isUpdate = false) {
      try {
        const response = isUpdate 
          ? await VideoProgressService.updateVideoProgress(payload)
          : await VideoProgressService.addVideoProgress(payload);
        return response;
      } catch (error) {
        console.error("Error saving video progress:", error);
        throw error;
      }
    },
  }), []);

  // Initialize component
  useEffect(() => {
    const initializeComponent = () => {
      const { privilege_view, privilege_add, privilege_edit, privilege_delete } = state || {};
      
      setPermissions({
        addVisibility: privilege_add === 1,
        editVisibility: privilege_edit === 1,
        viewVisibility: privilege_view === 1,
        deleteVisibility: privilege_delete === 1,
      });

      const node_mid_actual = state?.node_mid_actual !== null ? state.node_mid_actual : state?.mid;
      setNodeId(node_mid_actual);
    };

    initializeComponent();
  }, [state]);

  // Progress management functions
  const calculateTopicProgressFromVideoData = useCallback((progressData) => {
    if (!topicData?.videos || topicData.videos.length === 0) return 0;

    const totalDuration = topicData.videos.reduce((sum, video) => 
      sum + (parseFloat(video.duration) || 0), 0
    );
    const watchedDuration = topicData.videos.reduce((sum, video) => 
      sum + (progressData[video.video_id] || 0), 0
    );
    
    return totalDuration > 0 ? Math.min((watchedDuration / totalDuration) * 100, 100) : 0;
  }, [topicData?.videos]);

  const refreshTopicProgress = useCallback(async () => {
    if (!topicData?.topic_id || !userId) return;

    try {
      const topicProgressData = await progressService.getTopicProgress(topicData.topic_id, userId);
      if (topicProgressData?.data?.completion_percentage !== undefined) {
        setProgressState(prev => ({
          ...prev,
          topicProgress: topicProgressData.data.completion_percentage
        }));
      }
    } catch (error) {
      console.warn("Failed to refresh topic progress:", error);
    }
  }, [topicData?.topic_id, userId, progressService]);

  const initializeUntrackedVideos = useCallback(async (currentProgressData) => {
    if (!hasValidUserData || !topicData?.videos) return currentProgressData;

    const untrackedVideos = topicData.videos.filter(video => 
      !existingProgressRecords.current.has(video.video_id)
    );

    if (untrackedVideos.length === 0) return currentProgressData;

    console.log("Initializing untracked videos:", untrackedVideos.map(v => v.video_id));

    const initPromises = untrackedVideos.map(async (video) => {
      const payload = {
        user_id: userId,
        user_table_id: userTableId,
        topic_id: topicData.topic_id,
        video_id: video.video_id,
        watched_duration: 0,
        last_watched_at: new Date().toISOString(),
        total_duration: parseFloat(video.duration) || 0,
        is_completed: false,
      };

      try {
        await progressService.saveVideoProgress(payload, false);
        existingProgressRecords.current.add(video.video_id);
        lastSavedProgress.current[video.video_id] = 0;
        return { videoId: video.video_id, watchedDuration: 0 };
      } catch (error) {
        console.error("Error initializing video progress:", error);
        return { videoId: video.video_id, watchedDuration: 0 };
      }
    });

    const initResults = await Promise.all(initPromises);
    const updatedProgressData = { ...currentProgressData };
    
    initResults.forEach(result => {
      updatedProgressData[result.videoId] = result.watchedDuration;
    });

    setProgressState(prev => ({
      ...prev,
      videoProgress: updatedProgressData
    }));

    await refreshTopicProgress();
    return updatedProgressData;
  }, [hasValidUserData, topicData, userId, userTableId, progressService, refreshTopicProgress]);

  const loadProgressData = useCallback(async () => {
    if (!topicData?.videos || !userId) return;

    setProgressState(prev => ({ ...prev, isProgressLoading: true }));

    try {
      const videoProgressPromises = topicData.videos.map(async (video) => {
        const progressData = await progressService.getVideoProgress(userId, video.video_id);
        
        if (progressData?.data?.watched_duration !== undefined && progressData?.data?.id) {
          existingProgressRecords.current.add(video.video_id);
          return {
            videoId: video.video_id,
            watchedDuration: progressData.data.watched_duration,
          };
        }
        
        return { videoId: video.video_id, watchedDuration: 0 };
      });

      const progressResults = await Promise.all(videoProgressPromises);
      const progressObj = {};
      
      progressResults.forEach((result) => {
        progressObj[result.videoId] = result.watchedDuration;
        lastSavedProgress.current[result.videoId] = result.watchedDuration;
      });

      // Initialize untracked videos
      const finalProgressData = await initializeUntrackedVideos(progressObj);

      // Load topic progress
      let topicProgressValue = 0;
      if (topicData.topic_id) {
        const topicProgressData = await progressService.getTopicProgress(topicData.topic_id, userId);
        topicProgressValue = topicProgressData?.data?.completion_percentage || 
                           calculateTopicProgressFromVideoData(finalProgressData);
      } else {
        topicProgressValue = calculateTopicProgressFromVideoData(finalProgressData);
      }

      setProgressState(prev => ({
        ...prev,
        videoProgress: finalProgressData,
        topicProgress: topicProgressValue,
        isProgressLoading: false
      }));

    } catch (error) {
      console.error("Error loading progress data:", error);
      const initialProgress = {};
      topicData.videos.forEach((video) => {
        initialProgress[video.video_id] = 0;
      });
      
      setProgressState(prev => ({
        ...prev,
        videoProgress: initialProgress,
        topicProgress: 0,
        isProgressLoading: false
      }));
    }
  }, [topicData, userId, progressService, initializeUntrackedVideos, calculateTopicProgressFromVideoData]);

  const saveVideoProgress = useCallback(async (videoId, watchedDuration, isCompleted = false) => {
    if (!hasValidUserData || !videoId) return;

    const video = topicData?.videos?.find((v) => v.video_id === videoId);
    if (!video) return;

    // NEW: Check if video is already completed - if so, don't update progress
    const currentWatchedDuration = progressState.videoProgress[videoId] || 0;
    if (isVideoCompleted(currentWatchedDuration, video.duration) && !isCompleted) {
      return; // Don't update progress if video is already completed
    }

    const lastSaved = lastSavedProgress.current[videoId] || 0;
    const timeDifference = Math.abs(watchedDuration - lastSaved);

    if (timeDifference < PROGRESS_SAVE_THRESHOLD && !isCompleted) return;

    const payload = {
      user_id: userId,
      user_table_id: userTableId,
      topic_id: topicData.topic_id,
      video_id: videoId,
      watched_duration: watchedDuration,
      last_watched_at: new Date().toISOString(),
      total_duration: parseFloat(video.duration) || 0,
      is_completed: isCompleted,
    };

    try {
      const hasExistingProgress = existingProgressRecords.current.has(videoId);
      await progressService.saveVideoProgress(payload, hasExistingProgress);
      
      if (!hasExistingProgress) {
        existingProgressRecords.current.add(videoId);
      }
      
      lastSavedProgress.current[videoId] = watchedDuration;
      
      setProgressState(prev => ({
        ...prev,
        videoProgress: {
          ...prev.videoProgress,
          [videoId]: watchedDuration
        }
      }));
      
      await refreshTopicProgress();

    } catch (error) {
      if (error.message && error.message.includes("Progress already exists")) {
        existingProgressRecords.current.add(videoId);
        try {
          await progressService.saveVideoProgress(payload, true);
          lastSavedProgress.current[videoId] = watchedDuration;
          
          setProgressState(prev => ({
            ...prev,
            videoProgress: {
              ...prev.videoProgress,
              [videoId]: watchedDuration
            }
          }));
          
          await refreshTopicProgress();
        } catch (updateError) {
          console.error("Error updating video progress on retry:", updateError);
        }
      }
    }
  }, [hasValidUserData, topicData, userId, userTableId, progressService, refreshTopicProgress, progressState.videoProgress]);

  const handleProgressUpdate = useCallback((videoId, currentTime) => {
    // NEW: Check if video is already completed before updating
    const video = topicData?.videos?.find((v) => v.video_id === videoId);
    if (!video) return;

    const currentWatchedDuration = progressState.videoProgress[videoId] || 0;
    if (isVideoCompleted(currentWatchedDuration, video.duration)) {
      return; // Don't update progress if video is already completed
    }

    setProgressState(prev => ({
      ...prev,
      videoProgress: {
        ...prev.videoProgress,
        [videoId]: Math.max(prev.videoProgress[videoId] || 0, currentTime)
      }
    }));
    
    saveVideoProgress(videoId, currentTime);
  }, [saveVideoProgress, topicData?.videos, progressState.videoProgress]);

  // Custom hook for progress tracking
  const { startTracking, stopTracking } = useProgressTracking(
    videoRef, 
    videoState.selectedVideo, 
    handleProgressUpdate
  );

  // Load progress data when component mounts or dependencies change
  useEffect(() => {
    if (userId && topicData?.videos) {
      loadProgressData();
    }
  }, [userId, topicData?.videos, loadProgressData]);

  // Video management functions
  const initializeVideo = useCallback(async (video) => {
    if (!video) return;

    setVideoState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const cacheKey = `${video.container_id}_${video.video_name}`;
      
      // Check cache first
      if (streamUrlCache.current.has(cacheKey)) {
        const cachedData = streamUrlCache.current.get(cacheKey);
        setVideoState(prev => ({
          ...prev,
          videoStreamUrl: cachedData.streamUrl,
          videoMetadata: cachedData.metadata,
          availableQualities: cachedData.qualities,
          isLoading: false,
        }));

        setTimeout(() => {
          if (videoRef.current) {
            const savedProgress = progressState.videoProgress[video.video_id] || 0;
            videoRef.current.currentTime = savedProgress;
            videoRef.current.play().catch(console.error);
          }
        }, 100);
        return;
      }

      // Fetch video data
      const [exists, metadata, qualities, streamUrl] = await Promise.all([
        AzureStorageManage.checkVideoAvailability(video.container_id, video.video_name),
        AzureStorageManage.getVideoMetadata(video.container_id, video.video_name),
        AzureStorageManage.generateMultiQualityStreams(video.container_id, video.video_name),
        AzureStorageManage.generateVideoStreamUrl(video.container_id, video.video_name),
      ]);

      if (!exists) throw new Error("Video not found");

      // Cache the results
      streamUrlCache.current.set(cacheKey, { streamUrl, metadata, qualities });
      
      setVideoState(prev => ({
        ...prev,
        videoMetadata: metadata,
        availableQualities: qualities,
        videoStreamUrl: streamUrl,
        isLoading: false,
      }));

      setTimeout(() => {
        if (videoRef.current) {
          const savedProgress = progressState.videoProgress[video.video_id] || 0;
          videoRef.current.currentTime = savedProgress;
          videoRef.current.play().catch(console.error);
        }
      }, 100);

    } catch (err) {
      console.error("Video initialization error:", err);
      setVideoState(prev => ({
        ...prev,
        error: err.message || "Failed to load video",
        isLoading: false,
      }));
    }
  }, [progressState.videoProgress]);

  // Event handlers
  const handleVideoSelect = useCallback((video) => {
    stopTracking();
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    
    setVideoState(prev => ({
      ...prev,
      selectedVideo: video,
      progress: 0,
      buffered: 0,
    }));
    
    initializeVideo(video);
  }, [stopTracking, initializeVideo]);

  const handleQualityChange = useCallback(async (quality) => {
    if (!videoState.selectedVideo || videoState.isLoading) return;

    setVideoState(prev => ({ ...prev, selectedQuality: quality, isLoading: true }));

    try {
      const videoName = quality === "auto" || quality === "original"
        ? videoState.selectedVideo.video_name
        : videoState.selectedVideo.video_name.replace(/\.(mp4|avi|mov|wmv)$/i, `_${quality}.$1`);

      const streamUrl = await AzureStorageManage.generateVideoStreamUrl(
        videoState.selectedVideo.container_id, 
        videoName
      );

      if (videoRef.current) {
        const currentTime = videoRef.current.currentTime;
        videoRef.current.src = streamUrl;
        videoRef.current.currentTime = currentTime;
        videoRef.current.play().catch(console.error);
      }
      
      setVideoState(prev => ({
        ...prev,
        videoStreamUrl: streamUrl,
        isLoading: false,
      }));
    } catch (err) {
      setVideoState(prev => ({
        ...prev,
        error: "Failed to change quality",
        isLoading: false,
      }));
    }
  }, [videoState.selectedVideo, videoState.isLoading]);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setVideoState(prev => ({
        ...prev,
        progress: (videoRef.current.currentTime / videoRef.current.duration) * 100,
      }));
    }
  }, []);

  const handleProgress = useCallback(() => {
    if (videoRef.current?.buffered.length > 0) {
      const bufferedEnd = videoRef.current.buffered.end(videoRef.current.buffered.length - 1);
      setVideoState(prev => ({
        ...prev,
        buffered: (bufferedEnd / videoRef.current.duration) * 100,
      }));
    }
  }, []);

  const handleVideoEnded = useCallback(() => {
    stopTracking();
    if (videoState.selectedVideo) {
      const duration = parseFloat(videoState.selectedVideo.duration) || 0;
      setProgressState(prev => ({
        ...prev,
        videoProgress: {
          ...prev.videoProgress,
          [videoState.selectedVideo.video_id]: duration,
        }
      }));
      saveVideoProgress(videoState.selectedVideo.video_id, duration, true);
    }
  }, [videoState.selectedVideo, stopTracking, saveVideoProgress]);

  // Render functions
  const renderVideoItem = useCallback((video) => {
    const watchedDuration = progressState.videoProgress[video.video_id] || 0;
    const videoProgressPercentage = getVideoProgressPercentage(watchedDuration, video.duration);
    const isSelected = videoState.selectedVideo?.video_id === video.video_id;

    return (
      <div
        key={video.video_id}
        className={`video-item ${isSelected ? "selected" : ""}`}
        onClick={() => handleVideoSelect(video)}
      >
        <div className="video-thumbnail">
          <span>▶️</span>
          {videoProgressPercentage > 0 && (
            <div className="video-progress-indicator">
              <div className="video-progress-fill" style={{ width: `${videoProgressPercentage}%` }} />
            </div>
          )}
        </div>
        <div className="video-details">
          <h4 className="video-title">{video.video_name}</h4>
          <div className="video-meta">
            <span className="video-date">{formatDate(video.created_at)}</span>
            <span className="video-duration">{formatDuration(video.duration)}</span>
            {videoProgressPercentage > 0 && (
              <span className="video-progress-text">{videoProgressPercentage.toFixed(1)}% watched</span>
            )}
          </div>
        </div>
        {isSelected && (
          <div className="selection-indicator">
            <span>▶</span>
          </div>
        )}
      </div>
    );
  }, [progressState.videoProgress, videoState.selectedVideo, handleVideoSelect]);

  const renderVideoList = () => (
    <div className="video-list-container">
      <div className="video-list-header">
        <h3>Videos ({videoCount})</h3>
        {progressState.isProgressLoading && (
          <div className="progress-loading">
            <span>Loading progress...</span>
          </div>
        )}
      </div>
      <div className="video-list">
        {videoCount > 0 ? (
          topicData.videos.map(renderVideoItem)
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📹</div>
            <p>No videos available</p>
            <small>Videos will appear here once uploaded</small>
          </div>
        )}
      </div>
    </div>
  );

  const renderVideoPlayer = () => {
    if (!videoState.selectedVideo) {
      return (
        <div className="no-video-selected">
          <div className="no-video-icon">🎥</div>
          <h3>Select a video to play</h3>
          <p>Choose a video from the list to start watching</p>
        </div>
      );
    }

    return (
      <div className="video-player-wrapper">
        <div className="player-header">
          <h3 className="current-video-title">{videoState.selectedVideo.video_name}</h3>
          {Object.keys(videoState.availableQualities).length > 1 && (
            <div className="quality-selector">
              <select
                value={videoState.selectedQuality}
                onChange={(e) => handleQualityChange(e.target.value)}
                disabled={videoState.isLoading}
              >
                <option value="auto">Auto</option>
                {Object.keys(videoState.availableQualities).map((quality) => (
                  <option key={quality} value={quality}>
                    {quality.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {videoState.error ? (
          <div className="video-error">
            <div className="error-icon">⚠️</div>
            <h4>Playback Error</h4>
            <p>{videoState.error}</p>
            <button className="retry-button" onClick={() => handleVideoSelect(videoState.selectedVideo)}>
              Retry
            </button>
          </div>
        ) : (
          <div className="video-player">
            <div className="video-container">
              {videoState.videoStreamUrl && (
                <video
                  ref={videoRef}
                  src={videoState.videoStreamUrl}
                  controls
                  preload="metadata"
                  controlsList="nodownload"
                  onTimeUpdate={handleTimeUpdate}
                  onProgress={handleProgress}
                  onPlay={startTracking}
                  onPause={stopTracking}
                  onEnded={handleVideoEnded}
                  onError={() => setVideoState(prev => ({ ...prev, error: "Video playback failed" }))}
                  onContextMenu={(e) => e.preventDefault()} // Disable right-click context menu
                  disablePictureInPicture // Disable picture-in-picture mode
                  crossOrigin="anonymous" // Additional security measure
                />
              )}
              {videoState.isLoading && (
                <div className="loading-overlay">
                  <div className="loading-spinner">
                    <div className="spinner-circle" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {videoState.videoMetadata && (
          <div className="video-info">
            <div className="info-item">
              <span className="info-label">Duration:</span>
              <span className="info-value">{formatDuration(videoState.selectedVideo.duration)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Uploaded:</span>
              <span className="info-value">{formatDate(videoState.selectedVideo.created_at)}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      streamUrlCache.current.clear();
    };
  }, []);

  return (
    <ContentWrapper>
      <CardBody>
        <div className="video-stream-container">
          <div className="header-container">
            <button onClick={onBack} className="back-button">← Back</button>
            <div className="topic-info">
              <h1 className="topic-title">🎬 {topicData?.topic_name || "Untitled Topic"}</h1>
              <p className="topic-description">{topicData?.description}</p>
              <div className="topic-stats">
                <span className="video-count">
                {progressState.isProgressLoading ? (
                  <strong>Loading...</strong>
                ) : (
                  <>
                    Completed <strong>{completedVideosCount}</strong> of {videoCount} recordings. 📺
                  </>
                )}
              </span>

                <div className="topic-progress-container">
                  <div className="progress-info">
                    <span className="progress-label">Progress:</span>
                    <span className="progress-percentage">
                      {progressState.isProgressLoading ? "Loading..." : `${progressState.topicProgress.toFixed(1)}%`}
                    </span>
                  </div>
                  <div className="progress-bar-container">
                    <div className="progress-bar topic-progress-bar">
                      <div
                        className="progress-fill topic-progress-fill"
                        style={{ width: `${progressState.topicProgress}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="main-content">
            {renderVideoList()}
            <div className="video-player-container">
              {renderVideoPlayer()}
            </div>
          </div>
        </div>
      </CardBody>
    </ContentWrapper>
  );
}

export default ManageVideoStream;